import { defineStore } from "pinia";
import { ref, computed } from "vue";

const useLookup = (sourceRef) =>
  computed(() => {
    const map = new Map();

    sourceRef.value.forEach((item) => map.set(item.id, item));

    return map;
  });

export const useContext = defineStore("appContext", () => {
  const id = ref(null);
  const name = ref(null);
  const locale = ref(null);
  const idleLimit = ref(null);
  const printerId = ref(null);
  const rootTerminalFolderId = ref(null);
  const unreadNotificationCount = ref(0);
  const genericDiscountCommentMode = ref(null);
  const earlyArrivalLimit = ref(0);
  const hasClosingError = ref(false);

  const openingHours = ref({});
  const reservationFeatures = ref(new Set()); // Stored as Set for O(1) lookup

  const terminalFolders = ref([]);
  const floors = ref([]);
  const courses = ref([]);
  const bottomMenuItems = ref([]);
  const featuredTerminalFolders = ref([]);
  const storeKitchenHighlights = ref([]);
  const cancellationReasons = ref([]);
  const cashTransferCategories = ref([]);
  const customerSources = ref([]);
  const deliveryPartners = ref([]);
  const orderProviders = ref([]);
  const paymentMethods = ref([]);
  const products = ref([]);
  const bundles = ref([]);
  const bundleChoices = ref([]);
  const modifierGroups = ref([]);
  const allergens = ref([]);
  const denominations = ref([]);
  const orderStandardComments = ref([]);
  const orderLineStandardComments = ref([]);
  const currencies = ref([]);

  const hasAutomaticSeating = ref(false);
  const hasCourses = ref(false);
  const hasStock = ref(false);
  const hasReservations = ref(false);
  const hasManualGuestCount = ref(false);
  const hasEmailReceipts = ref(false);
  const hasStoreTasks = ref(false);
  const hasReturns = ref(false);
  const hasTips = ref(false);

  const setContext = (raw) => {
    if (!raw?.attributes) {
      return;
    }

    const attrs = raw.attributes;

    id.value = raw?.id ?? null;
    name.value = attrs.name;
    locale.value = attrs.locale;
    idleLimit.value = attrs.idle_limit;
    printerId.value = attrs.printer_id;
    rootTerminalFolderId.value = attrs.terminal_folder_id;
    unreadNotificationCount.value = attrs.unread_notification_count;
    genericDiscountCommentMode.value = attrs.generic_discount_comment_mode;
    earlyArrivalLimit.value = attrs.early_arrival_limit;
    hasClosingError.value = !!attrs.has_closing_error;

    openingHours.value = attrs.opening_hours ?? {};
    reservationFeatures.value = new Set(attrs.reservation_features ?? []);

    terminalFolders.value = attrs.terminal_folders ?? [];
    floors.value = attrs.floors ?? [];
    courses.value = attrs.courses ?? [];
    bottomMenuItems.value = attrs.bottom_menu_items ?? [];
    featuredTerminalFolders.value = attrs.featured_terminal_folders ?? [];
    storeKitchenHighlights.value = attrs.store_kitchen_highlights ?? [];
    cancellationReasons.value = attrs.cancellation_reasons ?? [];
    cashTransferCategories.value = attrs.cash_transfer_categories ?? [];
    customerSources.value = attrs.customer_sources ?? [];
    deliveryPartners.value = attrs.delivery_partners ?? [];
    orderProviders.value = attrs.order_providers ?? [];
    paymentMethods.value = attrs.terminal_payment_methods ?? [];
    products.value = attrs.products ?? [];
    bundles.value = attrs.bundles ?? [];
    bundleChoices.value = attrs.bundle_choices ?? [];
    modifierGroups.value = attrs.modifier_groups ?? [];
    allergens.value = attrs.allergens ?? [];
    denominations.value = attrs.denominations ?? [];
    orderStandardComments.value = attrs.standard_comments?.orders ?? [];
    orderLineStandardComments.value =
      attrs.standard_comments?.order_lines ?? [];
    currencies.value = attrs.currencies ?? [];

    hasAutomaticSeating.value = !!attrs.has_automatic_seating;
    hasCourses.value = !!attrs.has_courses;
    hasStock.value = !!attrs.has_stock;
    hasReservations.value = !!attrs.has_reservations;
    hasManualGuestCount.value = !!attrs.has_manual_guest_count;
    hasEmailReceipts.value = !!attrs.has_email_receipts;
    hasStoreTasks.value = !!attrs.has_store_tasks;
    hasReturns.value = !!attrs.has_returns;
    hasTips.value = !!attrs.has_tips;
  };

  const increaseUnreadNotifications = () => unreadNotificationCount.value++;
  const resetUnreadNotifications = () => (unreadNotificationCount.value = 0);

  const resolveClosingError = () => (hasClosingError.value = false);

  const hasReservationFeature = (feature) =>
    reservationFeatures.value.has(feature);

  const tables = computed(() => {
    return floors.value.flatMap((floor) =>
      floor.tables.map((table) => ({ ...table, floor_id: floor.id })),
    );
  });

  const bundleItems = computed(() => {
    return bundles.value.flatMap((bundle) => bundle.bundle_items || []);
  });

  const modifiers = computed(() => {
    return modifierGroups.value.flatMap(
      (modifierGroup) => modifierGroup.modifiers || [],
    );
  });

  const terminalFoldersById = useLookup(terminalFolders);
  const tablesById = useLookup(tables);
  const productsById = useLookup(products);
  const bundlesById = useLookup(bundles);
  const bundleItemsById = useLookup(bundleItems);
  const bundleChoicesById = useLookup(bundleChoices);
  const modifierGroupsById = useLookup(modifierGroups);
  const modifiersById = useLookup(modifiers);

  const smallestDenomination = computed(() => {
    if (denominations.value.length === 0) {
      return 1;
    }

    return Math.min(...denominations.value);
  });

  const rootTerminalFolder = computed(() =>
    rootTerminalFolderId.value
      ? getTerminalFolder(rootTerminalFolderId.value)
      : null,
  );

  const baseCurrency = computed(() =>
    currencies.value.find((currency) => currency.is_base),
  );

  const getTerminalFolder = (terminalFolderId) =>
    terminalFoldersById.value.get(terminalFolderId);
  const getTable = (tableId) => tablesById.value.get(tableId);
  const getProduct = (productId) => productsById.value.get(productId);
  const getBundle = (bundleId) => bundlesById.value.get(bundleId);
  const getBundleItem = (bundleItemId) =>
    bundleItemsById.value.get(bundleItemId);
  const getBundleChoice = (bundleChoiceId) =>
    bundleChoicesById.value.get(bundleChoiceId);
  const getModifierGroup = (modifierGroupId) =>
    modifierGroupsById.value.get(modifierGroupId);
  const getModifier = (modifierId) => modifiersById.value.get(modifierId);

  const openToday = computed(() => {
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const todayIndex = new Date().getDay();

    const todayName = dayNames[todayIndex];

    return openingHours.value[`${todayName}_opening`] != null;
  });

  return {
    // State
    id,
    name,
    locale,
    idleLimit,
    printerId,
    unreadNotificationCount,
    genericDiscountCommentMode,
    earlyArrivalLimit,
    hasClosingError,

    openingHours,

    floors,
    courses,
    bottomMenuItems,
    featuredTerminalFolders,
    storeKitchenHighlights,
    cancellationReasons,
    cashTransferCategories,
    customerSources,
    deliveryPartners,
    orderProviders,
    paymentMethods,
    products,
    bundles,
    modifierGroups,
    allergens,
    denominations,
    orderStandardComments,
    orderLineStandardComments,
    currencies,

    hasAutomaticSeating,
    hasCourses,
    hasStock,
    hasReservations,
    hasManualGuestCount,
    hasEmailReceipts,
    hasStoreTasks,
    hasReturns,
    hasTips,

    // Actions
    setContext,
    increaseUnreadNotifications,
    resetUnreadNotifications,
    resolveClosingError,

    // Getters
    hasReservationFeature,
    smallestDenomination,
    rootTerminalFolder,
    baseCurrency,
    tables,
    getTerminalFolder,
    getTable,
    getProduct,
    getBundle,
    getBundleItem,
    getBundleChoice,
    getModifierGroup,
    getModifier,
    openToday,
  };
});
